import {
  ExperienceProductTargetingDto,
  ProductExpressionPayload,
  ProductTargetingFeDisplayDataType,
  ProductTargetingType,
} from "@intelligems/ig-types";
import { expressionListToPostFix } from "@intelligems/ig-utils";
import * as rtk from "@reduxjs/toolkit";
import { type PayloadAction } from "@reduxjs/toolkit";
import { fromGqlId } from "@src/api/formatters/shopify";
import { initialExperienceProductTargetingState } from "@src/api/initials";
import { initialIExperienceProductTargetingState } from "@src/store/slices/experienceProductTargeting/types";
import {
  checkForExperienceProductTargetingExpression,
  splitProductTargetingValueString,
} from "@src/store/slices/experienceProductTargeting/utils";
import { BaseProduct } from "@src/store/slices/productPicker/types";

const experienceProductTargetingSlice = rtk.createSlice({
  name: "experienceProductTargeting",
  initialState: initialIExperienceProductTargetingState,
  reducers: {
    resetExperienceProductTargeting: (draft) => {
      draft = initialIExperienceProductTargetingState;
      return draft;
    },
    resetExperienceProductTargetingToStored: (draft) => {
      draft.experienceProductTargeting = draft.storedExperienceProductTargeting;
    },
    initExperienceProductTargeting: (
      draft,
      action: PayloadAction<{
        experienceProductTargeting: ExperienceProductTargetingDto[];
      }>
    ) => {
      draft.isSuccess = true;
      draft.experienceProductTargeting =
        action.payload.experienceProductTargeting;
      draft.storedExperienceProductTargeting =
        action.payload.experienceProductTargeting;
    },
    updateProductTargetingType: (
      draft,
      action: PayloadAction<{
        value: ProductTargetingType;
      }>
    ) => {
      if (draft.experienceProductTargeting.length) {
        draft.experienceProductTargeting[0].target = action.payload.value;
      }
    },
    removeTargetingValue: (
      draft,
      action: PayloadAction<{
        valueToRemove: string;
        expressionsListIndex: number;
      }>
    ) => {
      const { valueToRemove, expressionsListIndex } = action.payload;
      if (
        checkForExperienceProductTargetingExpression(
          draft.experienceProductTargeting
        )
      ) {
        const ensuredExpression =
          draft.experienceProductTargeting[0].expression!;
        if (ensuredExpression[expressionsListIndex].query !== undefined) {
          const splitValues = splitProductTargetingValueString(
            ensuredExpression[expressionsListIndex].query.value as string
          );
          const formattedValue =
            ensuredExpression[expressionsListIndex].query.type === "productId"
              ? fromGqlId(valueToRemove)
              : valueToRemove;
          draft.experienceProductTargeting[0].expression![
            expressionsListIndex
          ].query!.value = splitValues
            .filter((p) => p !== formattedValue)
            .join(",");

          // Also remove from displayData if it exists
          const query = draft.experienceProductTargeting[0].expression![
            expressionsListIndex
          ].query as {
            displayData?: Record<string, ProductTargetingFeDisplayDataType>;
          };
          if (query.displayData && query.displayData[formattedValue]) {
            delete query.displayData[formattedValue];
          }
        }
      }
    },
    updateProductTargetingValue: (
      draft,
      action: PayloadAction<{
        value: number | string;
        expressionIndex: number;
        displayData?: Record<string, ProductTargetingFeDisplayDataType>;
      }>
    ) => {
      if (
        checkForExperienceProductTargetingExpression(
          draft.experienceProductTargeting
        )
      ) {
        const ensuredExpression =
          draft.experienceProductTargeting[0].expression!;

        if (
          ensuredExpression[action.payload.expressionIndex].query !== undefined
        ) {
          draft.experienceProductTargeting[0].expression![
            action.payload.expressionIndex
          ].query!.value = action.payload.value;
          if (action.payload.displayData) {
            (
              draft.experienceProductTargeting[0].expression![
                action.payload.expressionIndex
              ].query as {
                displayData?: Record<string, ProductTargetingFeDisplayDataType>;
              }
            ).displayData = action.payload.displayData;
          }
        }
      }
    },
    updateProductTargetingProductIdValue: (
      draft,
      action: PayloadAction<{
        selectedProducts: BaseProduct[];
        expressionIndex: number;
      }>
    ) => {
      const { selectedProducts, expressionIndex } = action.payload;
      if (
        checkForExperienceProductTargetingExpression(
          draft.experienceProductTargeting
        )
      ) {
        const ensuredExpression =
          draft.experienceProductTargeting[0].expression!;

        if (ensuredExpression[expressionIndex].query !== undefined) {
          const displayData: Record<string, ProductTargetingFeDisplayDataType> =
            {};
          selectedProducts.forEach((product) => {
            displayData[product.id] = {
              title: product.title,
              image: product.image,
            };
          });
          const formattedValue: string = selectedProducts
            .map((product) => fromGqlId(product.id))
            .join(",");
          draft.experienceProductTargeting[0].expression![
            expressionIndex
          ].query = {
            ...draft.experienceProductTargeting[0].expression![expressionIndex]
              .query,
            type: "productId",
            key: "productId",
            filter:
              draft.experienceProductTargeting[0].expression![expressionIndex]
                .query!.filter,
            value: formattedValue,
            displayData: displayData,
          };
        }
      }
    },
    updateProductTargetingCollectionTagValue: (
      draft,
      action: PayloadAction<{
        selectedValues: { label: string; value: string }[];
        expressionIndex: number;
      }>
    ) => {
      const { selectedValues, expressionIndex } = action.payload;
      if (
        checkForExperienceProductTargetingExpression(
          draft.experienceProductTargeting
        )
      ) {
        const ensuredExpression =
          draft.experienceProductTargeting[0].expression!;

        if (ensuredExpression[expressionIndex].query !== undefined) {
          const displayData: Record<string, ProductTargetingFeDisplayDataType> =
            {};
          selectedValues.forEach((product) => {
            displayData[product.value] = {
              title: product.label,
            };
          });
          const formattedValue: string = selectedValues
            .map((product) => product.value)
            .join(",");
          draft.experienceProductTargeting[0].expression![
            expressionIndex
          ].query = {
            ...draft.experienceProductTargeting[0].expression![expressionIndex]
              .query,
            type: draft.experienceProductTargeting[0].expression![
              expressionIndex
            ].query!.type,
            key: draft.experienceProductTargeting[0].expression![
              expressionIndex
            ].query!.key,
            filter:
              draft.experienceProductTargeting[0].expression![expressionIndex]
                .query!.filter,
            value: formattedValue,
            displayData: displayData,
          };
        }
      }
    },
    mergeProductTargetingDisplayData: (
      draft,
      action: PayloadAction<{
        expressionIndex: number;
        displayData: Record<string, ProductTargetingFeDisplayDataType>;
      }>
    ) => {
      const { expressionIndex, displayData } = action.payload;
      if (
        checkForExperienceProductTargetingExpression(
          draft.experienceProductTargeting
        )
      ) {
        const query = draft.experienceProductTargeting[0].expression![
          expressionIndex
        ].query as {
          displayData?: Record<string, ProductTargetingFeDisplayDataType>;
        };
        if (query) {
          query.displayData = {
            ...query.displayData,
            ...displayData,
          };
        }
      }
    },

    updateExperienceProductTargeting: (
      draft,
      action: PayloadAction<{
        experienceId?: string;
        expressionsList: ProductExpressionPayload[];
      }>
    ) => {
      const postFix = expressionListToPostFix(
        action.payload.expressionsList,
        "experienceProductTargeting"
      );
      if (!postFix.length) {
        draft.experienceProductTargeting = [];
        return;
      }

      if (draft.experienceProductTargeting.length) {
        draft.experienceProductTargeting = [
          {
            ...draft.experienceProductTargeting[0],
            experienceId: action.payload.experienceId,
            expression: postFix,
          },
        ];
      } else {
        draft.experienceProductTargeting = [
          {
            ...initialExperienceProductTargetingState(
              action.payload.experienceId
            ),
            expression: postFix,
          },
        ];
      }
      return draft;
    },
  },
  selectors: {
    selectedExperienceProductTargetingState: (state) => state,
    getExperienceProductTargeting: (state) => state.experienceProductTargeting,
    getStoredExperienceProductTargeting: (state) =>
      state.storedExperienceProductTargeting,
  },
});

export const {
  resetExperienceProductTargeting,
  resetExperienceProductTargetingToStored,
  initExperienceProductTargeting,
  updateExperienceProductTargeting,
  updateProductTargetingType,
  updateProductTargetingValue,
  removeTargetingValue,
  updateProductTargetingProductIdValue,
  updateProductTargetingCollectionTagValue,
  mergeProductTargetingDisplayData,
} = experienceProductTargetingSlice.actions;

export const experienceProductTargetingReducer =
  experienceProductTargetingSlice.reducer;

export const {
  getExperienceProductTargeting,
  getStoredExperienceProductTargeting,
  selectedExperienceProductTargetingState,
} = experienceProductTargetingSlice.selectors;
